import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";

interface Activo {
  id: string;
  nombre: string;
  ciudad?: string;
  direccion?: string;
  tipo_explotacion?: string;
  coste_total?: number;
  valor_actual?: number;
  rentas_anuales?: number;
  hipoteca?: number;
  fecha_compra?: string;
  fecha_primer_alquiler?: string | null;
  vacancy_12m?: string;
  estado?: string;
  detalles?: string;
  notas?: string | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  activo?: Activo | null;
  setActivo?: (a: Partial<Activo>) => void;
  onSave: (updates: Partial<Activo>) => Promise<void>;
  saving?: boolean;
}

export function AdminActivosEditModal({ isOpen, onClose, activo, setActivo, onSave, saving = false }: Props) {
  const [local, setLocal] = useState<Partial<Activo>>({});

  const dbToUiTipo = (dbVal?: string | null) => {
    switch (dbVal) {
      case "Temporal":
        return "TEMPORAL";
      case "Habitaciones":
        return "HABITACIONES";
      case "Larga Temporada":
        return "TRADICIONAL";
      default:
        return "";
    }
  };

  const uiToDbTipo = (uiVal?: string | null): string | undefined => {
    switch (uiVal) {
      case "TEMPORAL":
        return "Temporal";
      case "HABITACIONES":
        return "Habitaciones";
      case "TRADICIONAL":
        return "Larga Temporada";
      default:
        return undefined;
    }
  };

  const dbToUiEstado = (dbVal?: string | null) => {
    switch (dbVal) {
      case "alquilado":
        return "CASHFLOWING";
      case "alquilándose":
        return "RENTING";
      case "en obras":
        return "REFORMA";
      case "en venta":
        return "ARRAS";
      default:
        return "";
    }
  };

  const uiToDbEstado = (uiVal?: string | null): string | undefined => {
    switch (uiVal) {
      case "CASHFLOWING":
        return "alquilado";
      case "RENTING":
        return "alquilándose";
      case "REFORMA":
        return "en obras";
      case "ARRAS":
        return "en venta";
      default:
        return undefined;
    }
  };

  useEffect(() => {
    if (activo) {
      setLocal({
        ...activo,
        tipo_explotacion: dbToUiTipo(activo.tipo_explotacion),
        estado: dbToUiEstado(activo.estado),
        detalles: activo.notas ?? activo.detalles ?? "",
      });
    } else setLocal({});
  }, [activo]);

  const onChange = (field: keyof Activo, value: any) => {
    // Update local (form uses UI codes for tipo_explotacion and estado)
    setLocal((p) => ({ ...p, [field]: value }));

    // When propagating to parent, convert UI values back to DB-format strings
    if (field === "tipo_explotacion") {
      const dbVal = uiToDbTipo(value);
      setActivo?.({ ...(activo || {}), tipo_explotacion: dbVal });
    } else if (field === "estado") {
      const dbVal = uiToDbEstado(value);
      setActivo?.({ ...(activo || {}), estado: dbVal });
    } else if (field === "detalles") {
      // parent stores this in DB as `notas`
      setActivo?.({ ...(activo || {}), notas: value });
    } else {
      setActivo?.({ ...(activo || {}), [field]: value });
    }
  };

  const handleSave = async () => {
    if (!activo) return;
    // Convert any UI-coded fields back to DB format before saving
    const payload: Partial<Activo> = { ...local };
    if (local.tipo_explotacion) payload.tipo_explotacion = uiToDbTipo(String(local.tipo_explotacion));
    if (local.estado) payload.estado = uiToDbEstado(String(local.estado));
    // convert UI 'detalles' to DB 'notas'
    if ((local as any).detalles !== undefined) {
      (payload as any).notas = (local as any).detalles;
      delete (payload as any).detalles;
    }

    await onSave(payload);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-[720px] max-h-[90vh] overflow-y-auto text-gray-900 border border-transparent shadow-lg" style={{ backgroundColor: '#fafafa' }}>
        <DialogHeader className="pb-4">
          <DialogTitle className="text-2xl" style={{ fontWeight: 600 }}>{activo ? 'Editar activo' : 'Crear nuevo activo'}</DialogTitle>
          <p className="text-sm text-gray-600 mt-2">Complete los datos del activo inmobiliario</p>
        </DialogHeader>

        <div className="space-y-6 py-6 px-6">
          {/* Información básica */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Información básica</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm block">Propiedad *</Label>
                <Input className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm" value={local.nombre || ""} onChange={(e) => onChange("nombre", e.target.value)} />
              </div>
              <div>
                <Label className="text-sm block">Ubicación *</Label>
                <Input className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm" value={local.ciudad || ""} onChange={(e) => onChange("ciudad", e.target.value)} />
              </div>
              <div>
                <Label className="text-sm block">Tipo</Label>
                <select
                  className="mt-2 h-10 w-full rounded-full bg-white px-4 text-sm border-0 shadow-sm"
                  value={local.tipo_explotacion || ""}
                  onChange={(e) => onChange("tipo_explotacion", e.target.value)}
                >
                  <option value="">TEMPORAL</option>
                  <option value="TEMPORAL">TEMPORAL</option>
                  <option value="HABITACIONES">HABITACIONES</option>
                  <option value="TRADICIONAL">TRADICIONAL</option>
                </select>
              </div>
            </div>
          </div>

          {/* Datos financieros */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Datos financieros</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label className="text-sm block">Inversión (€)</Label>
                <Input className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm" type="number" value={local.coste_total ?? ""} onChange={(e) => onChange("coste_total", Number(e.target.value))} />
              </div>
              <div>
                <Label className="text-sm block">Valor Actual (€)</Label>
                <Input className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm" type="number" value={local.valor_actual ?? ""} onChange={(e) => onChange("valor_actual", Number(e.target.value))} />
              </div>
              <div>
                <Label className="text-sm block">Rentas Anual (€)</Label>
                <Input className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm" type="number" value={local.rentas_anuales ?? ""} onChange={(e) => onChange("rentas_anuales", Number(e.target.value))} />
              </div>
              <div>
                <Label className="text-sm block">Hipoteca (€)</Label>
                <Input className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm" type="number" value={local.hipoteca ?? ""} onChange={(e) => onChange("hipoteca", Number(e.target.value))} />
              </div>
            </div>
          </div>

          {/* Fechas y métricas */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Fechas y métricas</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm block">Fecha Compra</Label>
                <Input className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm" type="date" value={local.fecha_compra ? String(local.fecha_compra).slice(0,10) : ""} onChange={(e) => onChange("fecha_compra", e.target.value)} />
              </div>
              <div>
                <Label className="text-sm block">Fecha 1º Alquiler</Label>
                <Input className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm" type="date" value={local.fecha_primer_alquiler ? String(local.fecha_primer_alquiler).slice(0,10) : ""} onChange={(e) => onChange("fecha_primer_alquiler", e.target.value)} />
              </div>
              <div>
                <Label className="text-sm block">Vacancy 12M (%)</Label>
                <Input className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm" value={local.vacancy_12m || ""} onChange={(e) => onChange("vacancy_12m", e.target.value)} />
              </div>
            </div>
          </div>

          {/* Estado y enlaces */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Estado y enlaces</h3>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-end">
              <div>
                <Label className="text-sm block">Estado</Label>
                <select
                  className="mt-2 h-10 w-full rounded-full bg-white px-4 text-sm border-0 shadow-sm"
                  value={local.estado || ""}
                  onChange={(e) => onChange("estado", e.target.value)}
                >
                  <option value="">Seleccionar</option>
                  <option value="ARRAS">ARRAS</option>
                  <option value="REFORMA">REFORMA</option>
                  <option value="RENTING">RENTING</option>
                  <option value="CASHFLOWING">CASHFLOWING</option>
                </select>
              </div>
              <div className="lg:col-span-2">
                <Label className="text-sm block">Ver detalle (URL)</Label>
                <Input className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm" type="url" placeholder="https://..." value={(local as any).detalles || ""} onChange={(e) => onChange("detalles", e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="pt-6 border-t border-gray-100 flex justify-between items-center">
          <div>
            <Button variant="ghost" className="h-10 px-4" onClick={onClose} disabled={saving}>Cancelar</Button>
          </div>
          <div className="flex items-center">
            <Button variant="black" className="h-12 px-6 rounded-full" onClick={handleSave} disabled={saving}>
              {saving ? (<span className="inline-flex items-center gap-2"><Spinner size={14} /> Guardando…</span>) : (activo ? 'Guardar cambios' : 'Guardar activo')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
